import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  Alert,
  AlertDescription,
  AlertIcon,
  useDisclosure,
  ModalFooter,
  useToast,
  Skeleton,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  ShareFundingComponentFragmentGQLType,
  PrincipalPaymentMetricsGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';
import { usePermissionGuard } from '~/core/PermissionGuard';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  errorToastAdminConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { useGetPrincipalPaymentMetricsByLoanIdQuery } from '~/packages/invoices/requests/invoices.gql.generated';
import { GetParticipantValleyIbsApiRecordsForFundingRequestDocumentGQLType } from '~/packages/loans/requests/ibsIntegration.gql.generated';
import {
  useMarkLenderShareComponentFundTransferDateMutation,
  GetResolvedLoanByIdDocumentGQLType,
  GetCurrentLiabilityAccountsAndAccrualsForLoanDocumentGQLType,
} from '~/packages/loans/requests/loans.gql.generated';
import {
  GetDailyAccountActivityDocumentGQLType,
  GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
  GetResolvedDetailedEntriesForFacilitiesDocumentGQLType,
} from '~/packages/transactions/requests/transactions.gql.generated';

const getDefaultDate = (params: {
  loanId: string;
  fundingComponent: ShareFundingComponentFragmentGQLType;
  principalPaymentMetrics: PrincipalPaymentMetricsGQLType | null;
  isFullBalancePaymentEnabled: boolean;
}): Date => {
  const {
    fundingComponent,
    principalPaymentMetrics,
    isFullBalancePaymentEnabled,
  } = params;
  const { fundTransferDate, postingDate } = fundingComponent;

  if (isFullBalancePaymentEnabled) {
    if (principalPaymentMetrics && principalPaymentMetrics.latestPaymentDate) {
      const latestPaymentDate = commonDate.fullDateStrToDate(
        principalPaymentMetrics.latestPaymentDate,
      );
      return latestPaymentDate;
    }
  }

  if (fundTransferDate) {
    return commonDate.fullDateStrToDate(fundTransferDate);
  }
  if (postingDate) {
    return commonDate.fullDateStrToDate(postingDate);
  }
  return new Date();
};

interface MarkLenderShareComponentFundedModalProps {
  loanId: string;
  fundingComponent: ShareFundingComponentFragmentGQLType;
  modalControls: ReturnType<typeof useDisclosure>;
  minDate: Date;
}
export const MarkLenderShareComponentFundedModal = ({
  loanId,
  fundingComponent,
  modalControls,
  minDate,
}: MarkLenderShareComponentFundedModalProps) => {
  const toast = useToast();

  const isFullBalancePaymentEnabled =
    usePermissionGuard(
      ({ flags }) => flags.productsInvoicesFullBalancePaymentPermissions,
    ) === 'allow';

  const [fundTransferDate, setFundTransferDate] = React.useState<Date | null>(
    null,
  );

  const principalPaymentMetricsQuery = useGetPrincipalPaymentMetricsByLoanIdQuery(
    {
      fetchPolicy: 'cache-and-network',
      variables: { loanId },
    },
  );
  if (principalPaymentMetricsQuery.error) {
    throw principalPaymentMetricsQuery.error;
  }

  useEffect(() => {
    const defaultDate = getDefaultDate({
      loanId,
      fundingComponent,
      principalPaymentMetrics:
        principalPaymentMetricsQuery.data?.result ?? null,
      isFullBalancePaymentEnabled,
    });
    setFundTransferDate(defaultDate);
  }, [
    fundingComponent,
    loanId,
    principalPaymentMetricsQuery.data,
    isFullBalancePaymentEnabled,
  ]);

  const [
    markLenderShareComponentFundTransferDate,
    { loading: isLoading },
  ] = useMarkLenderShareComponentFundTransferDateMutation({
    refetchQueries: [
      GetResolvedLoanByIdDocumentGQLType,
      GetDailyAccountActivityDocumentGQLType,
      GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
      GetResolvedDetailedEntriesForFacilitiesDocumentGQLType,
      GetParticipantValleyIbsApiRecordsForFundingRequestDocumentGQLType,
      GetCurrentLiabilityAccountsAndAccrualsForLoanDocumentGQLType,
    ],
  });

  const handleMarkAsFundedClick = async (): Promise<void> => {
    if (!fundTransferDate) {
      toast({
        ...successToastConfig,
        title: 'Lender funded date not selected',
      });
      return;
    }

    const { fundingRequestComponentId, lenderShareId } = fundingComponent;

    try {
      await markLenderShareComponentFundTransferDate({
        variables: {
          lenderShareId,
          fundingRequestComponentId,
          fundTransferDate: commonDate.dateToFullDateString(fundTransferDate),
        },
      });

      toast({
        ...successToastConfig,
        duration: 3000,
        title: fundingComponent.fundTransferDate
          ? 'Successfully updated fund transfer date for Lender share.'
          : 'Successfully marked fund transfer date for Lender share.',
      });

      modalControls.onClose();
    } catch (error) {
      toast({
        ...errorToastAdminConfig,
        duration: 8000,
        title: getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'If you are unable to resolve the error, contact the Finley support team for assistance.',
        }),
      });
    }
  };

  if (
    principalPaymentMetricsQuery.loading ||
    !principalPaymentMetricsQuery.data
  ) {
    return <Skeleton height="full" width="full" />;
  }

  return (
    <Modal {...modalControls} size="lg">
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>
          <Typography variant="section-subtitle">
            {fundingComponent.fundTransferDate ? 'Change' : 'Select'} Lender
            Funded Date
          </Typography>
          <Typography variant="base-muted">
            {fundingComponent.fundTransferDate
              ? 'Selecting a past date for "Mark Funded" sets the Lender Funded Date and may impact accounting records. Ensure accuracy before proceeding.'
              : "This date will be saved as the 'Lender Funded Date'. Note that this cannot occur prior to any expected or existing invoice payments."}
          </Typography>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleDatePicker
            minDate={minDate}
            dateFormat="MMMM dd, yyyy"
            selectedDate={fundTransferDate}
            setSelectedDate={setFundTransferDate}
            excludeWeekends
            excludeHolidays
            width="full"
          />

          {!!fundTransferDate && fundTransferDate > new Date() && (
            <Alert status="info" mt="20px">
              <AlertIcon />
              <AlertDescription fontWeight="semibold">
                You have selected a future funding date.
              </AlertDescription>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter gap="0.5rem" mt="1rem">
          <Button
            colorScheme="v2-aurora"
            onClick={modalControls.onClose}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            colorScheme="v2-aurora"
            onClick={async () => {
              await handleMarkAsFundedClick();
            }}
            isDisabled={!fundTransferDate}
            isLoading={isLoading}
          >
            {fundingComponent.fundTransferDate ? 'Apply Changes' : 'Submit'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
